import { Injectable } from '@angular/core';
import moment from 'moment';
import { lastValueFrom } from 'rxjs';
import { IAvailability, IBase, IBookingCreate, IListExportPage, IWalking } from 'src/app/models';
import { IBookingFilters } from 'src/app/filters';
import { BookingOrigin, ExpeditionStatus, ExpeditionType, ServiceType } from 'src/app/models/enums';
import { objectToCamel } from 'ts-case-convert';
import { BookingsHttpService } from './bookings.http.service';
import { IBookingUpdate } from 'src/app/models/booking/bookingUpdate';
import { IMultipleBookingOutcome } from 'src/app/models/booking/multipleBookingOutcome';
import { IBookingDetails } from 'src/app/models/booking/bookingDetails';
import { LiteralService } from '../literal/literal.service';
import { AuthService } from '../auth/auth.service';
import { WebSocketService } from '../web-socket/web-socket.service';
import { ReservationFailureReason } from 'src/app/models/enums/reservationFailureReason';

@Injectable({
  providedIn: 'root',
})
export class BookingsService {
  constructor(
    private bookingsHttpService: BookingsHttpService,
    private literalService: LiteralService,
    private authService: AuthService,
    private webSocketService: WebSocketService,
  ) {}

  availabilityOriginName(availability: IAvailability): string {
    return availability.walkingPickup?.name || availability.inStop.name || '';
  }

  availabilityDestinationName(availability: IAvailability): string {
    return availability.walkingDropoff?.name || availability.outStop.name || '';
  }

  availabilityDepartureTime(availability: IAvailability) {
    return moment(availability.inStop.dateTime).subtract(availability.walkingPickup?.time || 0, 'seconds').format('HH:mm');
  }

  availabilityArrivalTime(availability: IAvailability) {
    return moment(availability.outStop.dateTime).add(availability.walkingDropoff?.time || 0, 'seconds').format('HH:mm');
  }

  isAlternativeOnInitial(availability: IAvailability, newBooking: IBookingCreate) {
    return newBooking.originStopId !== availability.inStop.id;
  }

  isAlternativeOnFinal(availability: IAvailability, newBooking: IBookingCreate) {
    return newBooking.destinationStops[0].exitStop.id !== availability.outStop.id;
  }

  walkingMinutes(walking: IWalking) {
    return Math.max(1, Math.round(walking.time / 60));
  }

  walkingText(walking: IWalking) {
    return this.literalService.get('bookings.walkTime', true, {
      time: this.formatWalkingTime(walking.time),
      distance: this.formatWalkingDistance(walking.distance)
    });
  }

  private formatWalkingTime(seconds: number) {
    if (seconds < 60) return `${Math.round(seconds)} s`;
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes ? `${hours} h ${remainingMinutes} min` : `${hours} h`;
  }

  private formatWalkingDistance(meters: number) {
    if (meters < 1000) return `${Math.round(meters)} m`;
    return `${parseFloat((meters / 1000).toFixed(2))} km`;
  }

  async getBookings(size: number, page?: number) {
    const source$ = this.bookingsHttpService.getBookings(size, page);
    const response = await lastValueFrom(source$);
    const bookingArray = this.getData(response);
    return bookingArray;
  }

  async getBookingsFiltered(
    size: number,
    page: number,
    expeditionClients: IBase[],
    expeditionTowns: IBase[],
    expeditionTypes: ExpeditionType[],
    expeditionStatus: ExpeditionStatus[],
    cancelled: boolean,
    faults: boolean,
    services: IBase[],
    routes: IBase[],
    origin: string,
    destination: string,
    searchValue?: string,
    serviceTypes?: ServiceType[]
  ) {
    const source$ = this.bookingsHttpService.getBookingsFiltered(
      size,
      page,
      expeditionClients,
      expeditionTowns,
      expeditionTypes,
      expeditionStatus,
      origin,
      destination,
      cancelled,
      faults,
      services,
      routes,
      searchValue,
      serviceTypes
    );
    const response = await lastValueFrom(source$);
    const bookingArray = this.getData(response);
    return bookingArray;
  }

  async getBookingsByUser(
    userId: number,
    size: number,
    page: number,
    expeditionClients: IBase[],
    expeditionTowns: IBase[],
    expeditionTypes: ExpeditionType[],
    expeditionStatus: ExpeditionStatus[],
    cancelled: boolean,
    faults: boolean,
    services: IBase[],
    routes: IBase[],
    origin: string,
    destination: string,
    searchValue?: string,
    serviceTypes?: ServiceType[]
  ) {
    const source$ = this.bookingsHttpService.getBookingsByUser(
      userId,
      size,
      page,
      expeditionClients,
      expeditionTowns,
      expeditionTypes,
      expeditionStatus,
      origin,
      destination,
      cancelled,
      faults,
      services,
      routes,
      searchValue,
      serviceTypes
    );
    const response = await lastValueFrom(source$);
    const bookingArray = this.getData(response);
    return bookingArray;
  }

  async exportBookings(filters: IBookingFilters, searchValue?: string, visiblePage?: IListExportPage) {
    return lastValueFrom(this.bookingsHttpService.exportBookings(filters, searchValue, visiblePage));
  }

  async getBooking(bookingId: number, cancelled?: boolean) {
    const source$ = this.bookingsHttpService.getBooking(bookingId, cancelled);
    const response = await lastValueFrom(source$);
    const booking = objectToCamel(response as object);
    return booking;
  }

  async createBooking(createBookingRequest: IBookingCreate) {
    const source$ = this.bookingsHttpService.createBooking(createBookingRequest);
    const response = await lastValueFrom(source$);
    const booking = objectToCamel(response as object);
    return booking;
  }

  // The batch answers the operation to follow, and each booking reports its own outcome on it
  async multipleBooking(createBookingRequest: IBookingCreate[], journey: boolean): Promise<IMultipleBookingOutcome> {
    const source$ = this.bookingsHttpService.multipleBooking(createBookingRequest, journey);
    const operationId = (await lastValueFrom(source$)) as string;
    const ephemeralToken = (await this.authService.getEphemeralToken()) as string;
    const outcome = await this.webSocketService.trackOperation(
      operationId,
      ephemeralToken,
      undefined,
      async () => (await this.authService.getEphemeralToken()) as string,
    );
    if (outcome.error) throw outcome.error;
    return objectToCamel((outcome.result ?? {}) as object) as unknown as IMultipleBookingOutcome;
  }
  
  async updateBooking(updateBookingRequest: IBookingUpdate)  {
    const source$ = this.bookingsHttpService.updateBooking(updateBookingRequest);
    const response = await lastValueFrom(source$);
    const booking = objectToCamel(response as IBookingDetails);
    return booking;
  }

  async deleteBooking(bookingId: number, comment: string, sendNotification?: boolean, refund?: boolean, fullRefund?: boolean, related?: boolean, acceptedRestrictions?: ReservationFailureReason[]) {
    const source$ = this.bookingsHttpService.deleteBooking(bookingId, comment, sendNotification, refund, fullRefund, related, acceptedRestrictions);
    const response = await lastValueFrom(source$);
    const booking = objectToCamel(response as object);
    return booking;
  }

  async deleteMultipleBooking(bookingsToCancel: any[]) {
    const source$ = this.bookingsHttpService.deleteMultipleBookings(bookingsToCancel);
    const response = await lastValueFrom(source$);
    const booking = objectToCamel(response as object);
    return booking;
  }

  async updateMissed(bookingId: number) {
    const source$ = this.bookingsHttpService.updateMissed(bookingId);
    const response = await lastValueFrom(source$);
    const booking = objectToCamel(response as object);
    return booking;
  }

  async updateMissedSeat(bookingId: number, seatId: number) {
    const source$ = this.bookingsHttpService.updateMissedSeat(bookingId, seatId);
    const response = await lastValueFrom(source$);
    const booking = objectToCamel(response as object);
    return booking;
  }
  
  async updateKms(bookingId: number, kms: number) {
    const source$ = this.bookingsHttpService.updateKms(bookingId, kms);
    const response = await lastValueFrom(source$);
    const booking = objectToCamel(response as IBookingDetails);
    return booking;
  }

  async updateNotified(bookingId: number, isNotified: boolean) {
    const source$ = this.bookingsHttpService.updateNotified(bookingId, isNotified);
    const response = await lastValueFrom(source$);
    const booking = objectToCamel(response as object);
    return booking;
  }

  async isExceedingKmLimit(userId: number, inStopId?: number, outStopId?: number, serviceId?: number, nBookings?: number) {
    let isExceeded = false;
    if (inStopId && outStopId && serviceId && nBookings) {
      const source$ = this.bookingsHttpService.isExceedingKmLimit(userId, inStopId, outStopId, serviceId, nBookings);
      const response = await lastValueFrom(source$);
      isExceeded = response as boolean
    }
    return isExceeded;
  }

  async updateTicketStatus(bookingId: number, paid?: boolean, validated?: boolean) {
    const source$ = this.bookingsHttpService.updateTicketStatus(bookingId, paid, validated);
    const response = await lastValueFrom(source$);
    const booking = objectToCamel(response as object);
    return booking;
  }

  async reassignAvailability(bookingIds: number[], newAvailabilityId: number, sendNotification: boolean, recomputePrice?: boolean) {
    const source$ = this.bookingsHttpService.reassignAvailability(bookingIds, newAvailabilityId, sendNotification, recomputePrice);
    const response = await lastValueFrom(source$);
    const booking = objectToCamel(response as object);
    return booking;
  }

  async resendMail(bookingId: number, related: boolean) {
    const source$ = this.bookingsHttpService.resendMail(bookingId, related);
    const response = await lastValueFrom(source$);
    const booking = objectToCamel(response as object);
    return booking;
  }

  getChannelTooltipText(type: BookingOrigin, notified?: boolean, userPhone?: string) {
    switch (type) {
      case BookingOrigin.BackOffice:
        if (notified === null) {
          return `<strong>${this.literalService.get('bookings.name', true)}: </strong>${this.literalService.get('bookings.actions.notifiedBooking.types.call', true)}`;
        }
        return `<strong>${this.literalService.get('bookings.name', true)}: </strong>${this.literalService.get('bookings.actions.notifiedBooking.types.call', true)}</br><strong>${this.literalService.get('bookings.actions.notifiedBooking.types.notifiedText', true)}: </strong>${this.literalService.get(notified ? 'bookings.actions.notifiedBooking.types.notified' : 'bookings.actions.notifiedBooking.types.notNotified', true)}</br><strong>${this.literalService.get('bookings.actions.notifiedBooking.types.phone', true)}:</strong> ${userPhone || '-'}`;
      case  BookingOrigin.UserApp:
        return `<strong>${this.literalService.get('bookings.name', true)}: </strong>${this.literalService.get('bookings.actions.notifiedBooking.types.app', true)}`;
      case  BookingOrigin.WhatsApp:
        return `<strong>${this.literalService.get('bookings.name', true)}: </strong>${this.literalService.get('bookings.actions.notifiedBooking.types.whatsapp', true)}`;
      default:
        return `<strong>${this.literalService.get('bookings.name', true)}: </strong>${this.literalService.get('bookings.actions.notifiedBooking.types.call', true)}`;
    }
  };

  getData(response: any) {
    return response.data;
  }
}
